import { useIntl } from 'react-intl';
import { toast } from 'sonner';
import { AxiosProgressEvent } from 'axios';
import { DataRequest } from 'hooks/useDataRequest';
import { useCallback, useContext } from 'react';
import { ExportRequest } from 'hooks/useExportRequest';
import { DataResult, Series, Table, Totals } from 'models/data';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { api } from 'config/api';
import { createTestIdToasterMessage } from 'utils/createTestIdToasterMessage';
import { useAuthenticatedApiCall } from './api.service';

export enum Service {
  Marketplaces = '/marketplaces',
  Articles = '/articles',
  ArticleVisibility = '/article-visibility',
  Performance = '/performance',
  Countries = '/countries',
  Products = '/products',
  Categories = '/categories',
  Entities = '/entities',
  Benchmarking = '/benchmarking',
  Trading = '/trading',
  Demand = '/demand',
  Profitability = '/profitability',
  Price = '/price',
}

export enum TimeGranularity {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

export interface TimeFilter {
  fromDate: string | undefined;
  toDate: string | undefined;
  granularity: TimeGranularity | undefined;
}

export enum SortDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}

export interface Filters {
  brands?: string[];
  countries?: string[];
  marketplaces?: string[];
  categories?: string[];
  genders?: string[];
  ageGroups?: string[];
  seasons?: string[];
  productNumbers?: string[];
  articleEANs?: string[];
  fulfillmentMethods?: string[];
  colors?: string[];
  sizes?: string[];
  dateBy?: string;
  benchmark?: string;
}

export interface SortOptions {
  measureCode?: string | null;
  direction: SortDirection;
}

export interface FilterSet {
  timeFilter: boolean;
  creationTimeFilter: boolean;
  comparisonTimeFilter: boolean;
  benchmarkingTimeFilter: boolean;
  benchmarkingComparisonTimeFilter: boolean;
  benchmark: boolean;
  brands: boolean;
  countries: boolean;
  categories: boolean;
  genders: boolean;
  ageGroups: boolean;
  seasons: boolean;
  marketplaces: boolean;
  fulfillmentMethods: boolean;
  products: boolean;
  productNumbers: boolean;
  articleEANs: boolean;
  colors: boolean;
  sizes: boolean;
  groupByAttribute: boolean;
  dateBy: boolean;
}

export const DEFAULT_FILTER_SET: FilterSet = {
  timeFilter: true,
  creationTimeFilter: false,
  comparisonTimeFilter: true,
  benchmarkingTimeFilter: false,
  benchmarkingComparisonTimeFilter: false,
  benchmark: false,
  brands: true,
  countries: true,
  marketplaces: true,
  categories: false,
  genders: false,
  ageGroups: false,
  seasons: false,
  fulfillmentMethods: true,
  products: false,
  productNumbers: true,
  articleEANs: false,
  colors: false,
  sizes: false,
  groupByAttribute: true,
  dateBy: true,
};

export const BENCHMARKING_FILTER_SET: FilterSet = {
  ...DEFAULT_FILTER_SET,
  benchmarkingTimeFilter: true,
  benchmarkingComparisonTimeFilter: true,
  benchmark: true,
};

export const DEFAULT_PRO_FILTER_SET: FilterSet = {
  ...DEFAULT_FILTER_SET,
  categories: true,
  genders: true,
  ageGroups: true,
  seasons: true,
  colors: true,
  sizes: true,
};

export const GROUP_BY_ATTRIBUTES = ['chnCountry', 'prdBrand', 'chnMarketplace'] as const;
export type GroupByAttribute = (typeof GROUP_BY_ATTRIBUTES)[number] | null;

export function useDataService(service: Service, filterSet: FilterSet = DEFAULT_FILTER_SET) {
  const intl = useIntl();
  const { makeAuthenticatedApiCall } = useAuthenticatedApiCall({
    baseURL: `${api.API_URL}${service}`,
  });

  const { accountNumber } = useContext(TenantConfigContext);

  const adjustFilters = (dataRequest: DataRequest): DataRequest => {
    return {
      ...dataRequest,
      filters: {
        brands: filterSet.brands ? dataRequest.filters.brands : undefined,
        categories: filterSet.categories ? dataRequest.filters.categories : undefined,
        genders: filterSet.genders ? dataRequest.filters.genders : undefined,
        ageGroups: filterSet.ageGroups ? dataRequest.filters.ageGroups : undefined,
        seasons: filterSet.seasons ? dataRequest.filters.seasons : undefined,
        countries: filterSet.countries ? dataRequest.filters.countries : undefined,
        marketplaces: filterSet.marketplaces ? dataRequest.filters.marketplaces : undefined,
        productNumbers: filterSet.productNumbers ? dataRequest.filters.productNumbers : undefined,
        articleEANs: filterSet.articleEANs ? dataRequest.filters.articleEANs : undefined,
        fulfillmentMethods: filterSet.fulfillmentMethods ? dataRequest.filters.fulfillmentMethods : undefined,
        colors: filterSet.colors ? dataRequest.filters.colors : undefined,
        sizes: filterSet.sizes ? dataRequest.filters.sizes : undefined,
        dateBy: filterSet.dateBy ? dataRequest.filters.dateBy : undefined,
        benchmark: filterSet.benchmark ? dataRequest.filters.benchmark : undefined,
      },
      comparisonTimeFilter: filterSet.comparisonTimeFilter ? dataRequest.comparisonTimeFilter : null,
      benchmarkingTimeFilter: filterSet.benchmarkingTimeFilter ? dataRequest.benchmarkingTimeFilter : null,
      benchmarkingComparisonTimeFilter: filterSet.benchmarkingComparisonTimeFilter
        ? dataRequest.benchmarkingComparisonTimeFilter
        : null,
      timeFilter: filterSet.timeFilter ? dataRequest.timeFilter : null,
      creationTimeFilter: filterSet.creationTimeFilter ? dataRequest.creationTimeFilter : null,
    };
  };

  const adjustExportFilters = (exportRequest: ExportRequest): ExportRequest => ({
    ...exportRequest,
    filters: {
      brands: filterSet.brands ? exportRequest.filters.brands : undefined,
      categories: filterSet.categories ? exportRequest.filters.categories : undefined,
      genders: filterSet.genders ? exportRequest.filters.genders : undefined,
      ageGroups: filterSet.ageGroups ? exportRequest.filters.ageGroups : undefined,
      seasons: filterSet.seasons ? exportRequest.filters.seasons : undefined,
      countries: filterSet.countries ? exportRequest.filters.countries : undefined,
      marketplaces: filterSet.marketplaces ? exportRequest.filters.marketplaces : undefined,
      fulfillmentMethods: filterSet.fulfillmentMethods ? exportRequest.filters.fulfillmentMethods : undefined,
      productNumbers: filterSet.productNumbers ? exportRequest.filters.productNumbers : undefined,
      articleEANs: filterSet.articleEANs ? exportRequest.filters.articleEANs : undefined,
      colors: filterSet.colors ? exportRequest.filters.colors : undefined,
      sizes: filterSet.sizes ? exportRequest.filters.sizes : undefined,
      dateBy: filterSet.dateBy ? exportRequest.filters.dateBy : undefined,
    },
    timeFilter: filterSet.timeFilter ? exportRequest.timeFilter : null,
  });

  const makeDataFetcher =
    <DataType>(dataTypeUrl: string) =>
    async (endpoint: string, dataRequest: DataRequest) => {
      return await makeAuthenticatedApiCall<DataType>({
        url: `${dataTypeUrl}${endpoint ? '/' + endpoint : ''}`,
        method: 'post',
        data: adjustFilters(dataRequest),
        timeout: 120000,
      });
    };

  const makeReportFetcher =
    <DataType>(dataTypeUrl: string) =>
    async (endpoint: string, exportRequest: ExportRequest) => {
      const promise = makeAuthenticatedApiCall<DataType>({
        url: `${dataTypeUrl}/${endpoint}`,
        method: 'post',
        data: exportRequest,
        responseType: 'blob',
      });

      toast.promise(promise, {
        loading: createTestIdToasterMessage(
          intl.formatMessage({
            id: 'articleVisibility.export.progress',
          }),
          'toaster-loading'
        ),
        success: exportResp =>
          exportResp.error
            ? {
                type: 'error',
                message: createTestIdToasterMessage(
                  intl.formatMessage({
                    id: 'articleVisibility.export.error',
                  }),
                  'toaster-error'
                ),
              }
            : {
                message: createTestIdToasterMessage(
                  intl.formatMessage({
                    id: 'articleVisibility.export.success',
                  }),
                  'toaster-success'
                ),
              },
        error: intl.formatMessage({ id: 'articleVisibility.export.error' }),
      });

      return await promise;
    };

  const makeExportCountFetcher = (dataTypeUrl: string) => async (endpoint: string, exportRequest: ExportRequest) => {
    return await makeAuthenticatedApiCall<number>({
      url: `${dataTypeUrl}/${endpoint}`,
      method: 'post',
      data: adjustExportFilters(exportRequest),
    });
  };

  const downloadFile = (endpoint: string) =>
    makeAuthenticatedApiCall<Blob>({
      url: endpoint,
      method: 'post',
      responseType: 'blob',
    });

  const uploadFile = <DataType>(
    endpoint: string,
    file: File,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
    signal?: AbortSignal
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileName', file.name);

    return makeAuthenticatedApiCall<DataType>({
      url: endpoint,
      method: 'post',
      data: formData,
      // let the browser set the correct multipart boundary instead of the default 'application/json'
      headers: { 'content-type': undefined },
      onUploadProgress,
      signal,
    });
  };

  return {
    fetchTotals: useCallback(makeDataFetcher<Totals>('totals'), [accountNumber]),
    fetchTable: useCallback(makeDataFetcher<Table>('table'), [accountNumber]),
    fetchTimeSeries: useCallback(makeDataFetcher<Series>('time-series'), [accountNumber]),
    initiateExport: useCallback(makeReportFetcher<Blob>('export-pro'), [accountNumber]),
    initiateArticleCount: useCallback(makeExportCountFetcher('article-count'), [accountNumber]),
    downloadFile: useCallback(downloadFile, [accountNumber]),
    uploadFile: useCallback(uploadFile, [accountNumber]),
    fetchData: useCallback(makeDataFetcher<DataResult>('data'), [accountNumber]),
    get: <T>(endpoint: string) => makeDataFetcher<T>(endpoint),
  };
}

export function useProDataService(service: Service, filterSet: FilterSet = DEFAULT_PRO_FILTER_SET) {
  return useDataService(service, filterSet);
}
